import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Teachers from "@/pages/Teachers";
import Classes from "@/pages/Classes";
import Attendance from "@/pages/Attendance";
import Assessments from "@/pages/Assessments";
import Finance from "@/pages/Finance";
import Transport from "@/pages/Transport";
import Messages from "@/pages/Messages";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import UserManagement from "@/pages/UserManagement";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helper function to check permissions
const hasPermission = (user, permission) => {
  if (!user) return false;
  if (user.role === "super_admin") return true; // Super admin has all permissions
  return user.permissions && user.permissions[permission];
};

const isAdminOrHigher = (user) => {
  return user && (user.role === "admin" || user.role === "super_admin");
};

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F9F9F7]">
        <div className="text-lg text-[#1E624A]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/" />
              ) : (
                <Login setUser={setUser} />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                <Layout user={user} setUser={setUser}>
                  <Dashboard user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/students"
            element={
              user ? (
                (user.role === "admin" || user.role === "super_admin" || (user.permissions && user.permissions.students)) ? (
                  <Layout user={user} setUser={setUser}>
                    <Students user={user} />
                  </Layout>
                ) : (
                  <Navigate to="/" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/teachers"
            element={
              user ? (
                hasPermission(user, "teachers") ? (
                  <Layout user={user} setUser={setUser}>
                    <Teachers />
                  </Layout>
                ) : (
                  <Navigate to="/" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/classes"
            element={
              user ? (
                <Layout user={user} setUser={setUser}>
                  <Classes user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/attendance"
            element={
              user ? (
                <Layout user={user} setUser={setUser}>
                  <Attendance user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/assessments"
            element={
              user ? (
                <Layout user={user} setUser={setUser}>
                  <Assessments user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/finance"
            element={
              user ? (
                hasPermission(user, "finance") ? (
                  <Layout user={user} setUser={setUser}>
                    <Finance />
                  </Layout>
                ) : (
                  <Navigate to="/" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/transport"
            element={
              user ? (
                hasPermission(user, "transport") ? (
                  <Layout user={user} setUser={setUser}>
                    <Transport />
                  </Layout>
                ) : (
                  <Navigate to="/" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/messages"
            element={
              user ? (
                <Layout user={user} setUser={setUser}>
                  <Messages user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/reports"
            element={
              user ? (
                hasPermission(user, "reports") ? (
                  <Layout user={user} setUser={setUser}>
                    <Reports />
                  </Layout>
                ) : (
                  <Navigate to="/" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings"
            element={
              user ? (
                <Layout user={user} setUser={setUser}>
                  <Settings user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/user-management"
            element={
              user ? (
                user.role === "super_admin" ? (
                  <Layout user={user} setUser={setUser}>
                    <UserManagement />
                  </Layout>
                ) : (
                  <Navigate to="/" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
